<script setup lang="ts">
import { Briefcase, X } from 'lucide-vue-next'
import type { FileToCaseRequest } from '~/types/web-chat'

const props = defineProps<{ request: FileToCaseRequest }>()

const emit = defineEmits<{
  'confirm-file-to-case': []
  'cancel-file-to-case': []
}>()

const { t } = useI18n()

/**
 * Documents the agent proposes to add to a case (MAR-988). Accepting LINKS them to the case; the files
 * stay where they are in the library, and the body says so, so the user is not surprised either way.
 *
 * The case name is the APP's, resolved against the id the link will be written to — never the
 * model's (`useConfirmCards`' `resolveFileToCaseCard`). Until it resolves, or if it does not, there is
 * nothing the user can safely confirm, so accepting is disabled.
 *
 * The settled line reads `request.outcome` — what the linking actually did (MAR-957).
 */
const settledLine = computed(() => {
  const { status, outcome } = props.request
  if (status === 'cancelled') return { text: t('webChat.confirmCards.fileToCase.cancelled'), failed: false }
  if (!outcome) return { text: t('webChat.confirmCards.fileToCase.working'), failed: false }
  if (outcome.ok) return { text: t('webChat.confirmCards.fileToCase.done', { case: props.request.caseName }), failed: false }
  if ((outcome.succeeded ?? 0) > 0) {
    return {
      text: t('webChat.confirmCards.fileToCase.partial', { done: outcome.succeeded ?? 0, failed: outcome.failed ?? 0 }),
      failed: true,
    }
  }
  return { text: t('webChat.confirmCards.fileToCase.failed'), failed: true }
})

const canAccept = computed(() => !props.request.loading && !props.request.error && props.request.items.length > 0)
</script>

<template>
  <div class="mt-3 rounded-lg border border-primary/20 bg-primary-lighter p-3">
    <div class="flex items-start gap-2">
      <div class="mt-0.5 h-5 w-5 rounded-full bg-primary-light flex items-center justify-center shrink-0">
        <Briefcase class="h-3 w-3 text-primary" />
      </div>
      <div class="min-w-0 flex-1">
        <p class="text-xs font-medium text-foreground">{{ t('webChat.confirmCards.fileToCase.title') }}</p>
        <i18n-t keypath="webChat.confirmCards.fileToCase.body" tag="p" class="mt-0.5 text-xs text-muted-foreground leading-relaxed" scope="global">
          <template #target>
            <span class="font-medium text-foreground">
              {{ request.items.length === 1 ? `"${request.items[0]?.name}"` : t('webChat.confirmCards.itemCount', { n: request.items.length }) }}
            </span>
          </template>
          <template #case>
            <span class="font-medium text-foreground">{{ request.loading ? t('webChat.confirmCards.fileToCase.loading') : request.caseName }}</span>
          </template>
        </i18n-t>
        <div
          v-if="request.items.length > 1"
          class="mt-1 text-[11px] text-muted-foreground/80 leading-relaxed"
        >
          {{ request.items.slice(0, 3).map(item => item.name).join(', ') }}{{ request.items.length > 3 ? ` ${t('webChat.confirmCards.andMore', { n: request.items.length - 3 })}` : '' }}
        </div>
        <p class="mt-1 text-[11px] text-muted-foreground/80">{{ t('webChat.confirmCards.fileToCase.note') }}</p>
        <p v-if="request.error" class="mt-1 text-[11px] text-destructive">{{ request.error }}</p>
        <div
          v-if="request.status === 'pending'"
          class="mt-2 flex flex-wrap gap-1.5"
        >
          <Button
            type="button"
            size="sm"
            class="gap-1.5"
            :disabled="!canAccept"
            @click="emit('confirm-file-to-case')"
          >
            <Briefcase class="h-3 w-3" />
            {{ t('webChat.confirmCards.fileToCase.action') }}
          </Button>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent-lighter hover:text-foreground transition-colors"
            @click="emit('cancel-file-to-case')"
          >
            <X class="h-3 w-3" />
            {{ t('webChat.confirmCards.cancel') }}
          </button>
        </div>
        <p
          v-else
          class="mt-2 text-[11px]"
          :class="settledLine.failed ? 'text-destructive' : 'text-muted-foreground'"
        >
          {{ settledLine.text }}
        </p>
      </div>
    </div>
  </div>
</template>
